import { Token } from '@ui/types/token';
import { TxnMutateCallBackParams } from '@ui/types/tx';
import { Address } from 'viem';

import { useExchangeQuote } from './hooks/useExchangeQuote';
import {
  ExactOutQuoteResult,
  useExchangeQuoteExactOutput,
} from './hooks/useExchangeQuoteExactOutput';
import { useSwap } from './hooks/useSwap';

export interface QuoteResult {
  bestCandidate: {
    adapter: Address;
    data: `0x${string}`;
  };
  amountOut: bigint;
  formattedAmountOut: string;
}

export interface SwapArgs {
  inputToken: Token;
  outputToken: Token;
  inputAmount: string;
  minOutputAmount: string;
  adapter: Address;
  adapterData: `0x${string}`;
  receiver: Address;
}

export interface QuoteParams {
  inputToken?: Token;
  outputToken?: Token;
  inputAmount: string;
}

export interface ExactOutQuoteParams {
  inputToken?: Token;
  outputToken?: Token;
  outputAmount: string;
}

export interface ExchangeExecutor {
  useQuote(params: QuoteParams): {
    data?: QuoteResult | null;
    isLoading: boolean;
  };

  useQuoteExactOut?(params: ExactOutQuoteParams): {
    data?: ExactOutQuoteResult | null;
    isLoading: boolean;
  };
  
  useSwap(params?: TxnMutateCallBackParams): {
    mutate: (args: SwapArgs, options?: any) => void;
    isPending: boolean;
  };

  getSwapArgs(params: {
    inputToken: Token;
    outputToken: Token;
    inputAmount: string;
    quoteData: QuoteResult;
    address: Address;
  }): SwapArgs;
}

export const SwapExecutor: ExchangeExecutor = {
  useQuote: useExchangeQuote,
  useQuoteExactOut: useExchangeQuoteExactOutput,
  useSwap: useSwap,
  getSwapArgs: ({ inputToken, outputToken, inputAmount, quoteData, address }) => ({
    inputToken,
    outputToken,
    inputAmount,
    minOutputAmount: quoteData.formattedAmountOut,
    adapter: quoteData.bestCandidate.adapter,
    adapterData: quoteData.bestCandidate.data,
    receiver: address,
  }),
};

export const RedeemExecutor: ExchangeExecutor = {
  useQuote: ({ inputToken, outputToken, inputAmount }) => {
    // Mock quote for 1:1 redeem
    const quoteData: QuoteResult | null = inputAmount ? {
      amountOut: BigInt(0), // TODO: correct conversion
      formattedAmountOut: inputAmount,
      bestCandidate: { adapter: '0x0000000000000000000000000000000000000000', data: '0x' }, // Dummy data
    } : null;
    
    return {
      data: quoteData,
      isLoading: false,
    };
  },
  useQuoteExactOut: ({ outputAmount }) => {
    // Mock quote for 1:1 redeem in reverse direction
    const quoteData: ExactOutQuoteResult | null = outputAmount
      ? {
          amountIn: 0n,
          formattedAmountIn: outputAmount,
          amountOut: 0n,
          formattedAmountOut: outputAmount,
          bestCandidate: {
            adapter: '0x0000000000000000000000000000000000000000',
            data: '0x',
          },
        }
      : null;

    return {
      data: quoteData,
      isLoading: false,
    };
  },
  useSwap: useSwap, // TODO: Replace with actual Redeem hook if needed
  getSwapArgs: ({ inputToken, outputToken, inputAmount, quoteData, address }) => ({
    // TODO: Implement actual redeem args
    inputToken,
    outputToken,
    inputAmount,
    minOutputAmount: inputAmount, // 1:1
    adapter: '0x0000000000000000000000000000000000000000' as Address, // Mock
    adapterData: '0x',
    receiver: address,
  }),
};
